import React from 'react';
import { 
  Chip, 
  Box, 
  IconButton, 
  Tooltip, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress
} from '@mui/material';
import {
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  CheckCircle as ConnectedIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useWebSocketStatus } from '../../hooks/useWebSocket';

interface ConnectionStatusProps {
  showText?: boolean;
  variant?: 'chip' | 'icon' | 'detailed';
  size?: 'small' | 'medium' | 'large';
  showReconnectButton?: boolean;
}

const ConnectionStatus: React.FC<ConnectionStatusProps> = ({
  showText = true,
  variant = 'chip',
  size = 'medium',
  showReconnectButton = true
}) => {
  const { status, loading, error, refetch } = useWebSocketStatus();
  const [showDetails, setShowDetails] = React.useState(false);

  // Получение конфигурации для каждого статуса
  const getStatusConfig = () => {
    if (!status) {
      return {
        icon: <WifiOffIcon />,
        label: 'Неизвестно',
        color: 'default' as const,
        bgColor: '#9e9e9e',
        textColor: '#fff'
      };
    }

    switch (status.status) {
      case 'online':
        return {
          icon: <ConnectedIcon />,
          label: 'Подключено',
          color: 'success' as const,
          bgColor: '#4caf50',
          textColor: '#fff'
        };
      case 'maintenance':
        return {
          icon: <WifiIcon />,
          label: 'Обслуживание',
          color: 'warning' as const,
          bgColor: '#ff9800',
          textColor: '#fff'
        };
      case 'offline':
      default:
        return {
          icon: <ErrorIcon />,
          label: 'Отключено',
          color: 'error' as const,
          bgColor: '#f44336',
          textColor: '#fff'
        };
    }
  };

  const config = getStatusConfig();

  // Обработчик переподключения
  const handleReconnect = async () => {
    try {
      await refetch();
    } catch (err) {
      console.error('Ошибка переподключения:', err);
    }
  };

  // Получение размера иконки
  const getIconSize = () => {
    switch (size) {
      case 'small':
        return 16;
      case 'large':
        return 24;
      default:
        return 20;
    }
  };

  // Рендер варианта "chip"
  if (variant === 'chip') {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Chip
          icon={config.icon}
          label={showText ? config.label : undefined}
          color={config.color}
          size={size === 'large' ? 'medium' : 'small'}
          onClick={() => setShowDetails(true)}
          sx={{
            cursor: 'pointer',
            '& .MuiChip-icon': {
              fontSize: getIconSize()
            }
          }}
        />
        
                 {showReconnectButton && status?.status !== 'online' && (
           <Tooltip title="Переподключиться">
             <IconButton 
               size="small" 
               onClick={handleReconnect}
               disabled={loading}
             >
               <RefreshIcon fontSize="small" />
             </IconButton>
           </Tooltip>
         )}

        {/* Модальное окно с деталями */}
        <Dialog open={showDetails} onClose={() => setShowDetails(false)}>
          <DialogTitle>Статус WebSocket соединения</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" alignItems="center" gap={1}>
                {config.icon}
                <Typography variant="body1" fontWeight="bold">
                  {config.label}
                </Typography>
              </Box>
              
              {error && (
                <Typography variant="body2" color="error">
                  Ошибка: {error}
                </Typography>
              )}
              
              <Typography variant="body2" color="text.secondary">
                WebSocket обеспечивает real-time обновления уведомлений и аналитики.
              </Typography>
              
                             {loading && (
                 <LinearProgress />
               )}
            </Box>
          </DialogContent>
          <DialogActions>
                         {status?.status !== 'online' && (
               <Button onClick={handleReconnect} variant="outlined">
                 Переподключиться
               </Button>
             )}
            <Button onClick={() => setShowDetails(false)}>
              Закрыть
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Рендер варианта "icon"
  if (variant === 'icon') {
    return (
      <Tooltip title={`${config.label}${error ? `: ${error}` : ''}`}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={getIconSize() + 8}
          height={getIconSize() + 8}
          borderRadius="50%"
          bgcolor={config.bgColor}
          color={config.textColor}
          sx={{ cursor: 'pointer' }}
          onClick={() => setShowDetails(true)}
        >
          {React.cloneElement(config.icon, { 
            fontSize: size === 'small' ? 'small' : 'medium' 
          })}
        </Box>
      </Tooltip>
    );
  }

  // Рендер варианта "detailed"
  return (
    <Box 
      display="flex" 
      alignItems="center" 
      gap={1}
      p={1}
      borderRadius={1}
      bgcolor={config.bgColor}
      color={config.textColor}
    >
      {config.icon}
      {showText && (
        <Typography variant="body2" fontWeight="medium">
          {config.label}
        </Typography>
      )}
      {error && (
        <Tooltip title={error}>
          <WarningIcon fontSize="small" />
        </Tooltip>
      )}
             {showReconnectButton && status?.status !== 'online' && (
         <IconButton
           size="small"
           onClick={handleReconnect}
           disabled={loading}
           sx={{ color: 'inherit' }}
         >
           <RefreshIcon fontSize="small" />
         </IconButton>
       )}
    </Box>
  );
};

export default ConnectionStatus; 